import React, {useEffect, useRef, useState} from 'react';
import {trackCommunity} from './analytics';
import './citizenScienceWelcome.css';

const mediaRoot = '/citizen-science-welcome/v1';
const tileGroups = [[5, 7, 11], [0, 6, 18], [2, 8, 15], [3, 9, 16], [1, 10, 19], [4, 12, 17], [6, 13, 14]];
const initialTiles = tileGroups[0];

// One decoded video supplies the entire mosaic and the three raised crops.
// We never mount twenty independent video players on a phone.
function WildlifeMosaic({active: screenActive}) {
  const video = useRef(null);
  const canvases = useRef([]);
  const [tiles, setTiles] = useState(initialTiles);
  const [moving, setMoving] = useState(false);
  useEffect(() => {
    const element = video.current;
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const connection = navigator.connection;
    let timer, frame, lastDraw = 0, active = true, generation = 0;
    const draw = time => {
      if (!active || !screenActive || document.hidden || motion.matches || connection?.saveData) return;
      if (element.readyState >= 2 && time - lastDraw >= 100) {
        lastDraw = time;
        canvases.current.forEach(canvas => {
          if (!canvas) return;
          const tile = Number(canvas.dataset.tile);
          canvas.getContext('2d')?.drawImage(element, (tile % 4) * 164 + 2, Math.floor(tile / 4) * 164 + 2, 160, 160, 0, 0, 240, 240);
        });
      }
      frame = requestAnimationFrame(draw);
    };
    const stop = () => {element.pause(); cancelAnimationFrame(frame); clearInterval(timer); setMoving(false);};
    const start = () => {
      const current = ++generation;
      stop();
      if (!screenActive || document.hidden || motion.matches || connection?.saveData) return;
      if (!element.getAttribute('src')) element.src = `${mediaRoot}/wildlife-mosaic.mp4`;
      element.muted = true;
      element.play().then(() => {
        if (!active || !screenActive || current !== generation || document.hidden || motion.matches || connection?.saveData) return;
        setMoving(true); frame = requestAnimationFrame(draw);
        let step = 0;
        timer = setInterval(() => {step++; setTiles(tileGroups[step % tileGroups.length]);}, 6000);
      }).catch(() => {}); // Poster remains visible when iOS blocks decorative autoplay.
    };
    start();
    document.addEventListener('visibilitychange', start);
    motion.addEventListener?.('change', start);
    connection?.addEventListener?.('change', start);
    return () => {
      active = false; element.pause(); cancelAnimationFrame(frame); clearInterval(timer);
      document.removeEventListener('visibilitychange', start);
      motion.removeEventListener?.('change', start);
      connection?.removeEventListener?.('change', start);
    };
  }, [screenActive]);
  return <div className={`cs-welcome-mosaic${moving ? ' is-moving' : ''}`} aria-hidden="true">
    <video ref={video} poster={`${mediaRoot}/wildlife-mosaic.jpg`} muted loop playsInline preload="none" disablePictureInPicture tabIndex={-1} />
    {tiles.map((tile, index) => <div key={tile} className={`cs-welcome-pop cs-welcome-pop-${index}`} style={{
      '--tile-column': tile % 4, '--tile-row': Math.floor(tile / 4),
      '--origin-x': tile % 4 === 0 ? '0%' : tile % 4 === 3 ? '100%' : '50%',
      '--origin-y': tile < 4 ? '0%' : tile >= 16 ? '100%' : '50%',
      backgroundImage: `url(${mediaRoot}/wildlife-mosaic.jpg)`,
      backgroundPosition: `${(tile % 4) / 3 * 100}% ${Math.floor(tile / 4) / 4 * 100}%`,
    }}><canvas ref={value => {canvases.current[index] = value;}} data-tile={tile} width="240" height="240" /></div>)}
  </div>;
}

export default function CitizenScienceWelcome({api, preview = false, onTutorial, onStartSpotting, active = true}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const lock = useRef(false);
  const alive = useRef(true);
  const trackedView = useRef(false);
  useEffect(() => {
    alive.current = true;
    return () => {alive.current = false;};
  }, []);
  useEffect(() => {
    if (active && !trackedView.current) {
      trackedView.current = true;
      trackCommunity('cs_introduction_viewed', {test_mode: preview});
    }
  }, [active, preview]);
  const startSpotting = async () => {
    if (!active || lock.current) return;
    lock.current = true; setSaving(true); setError('');
    try {
      if (!preview) await api.skip();
      if (!alive.current) return;
      trackCommunity('cs_introduction_skipped', {test_mode: preview});
      onStartSpotting();
    } catch (_) {
      if (alive.current) setError('We couldn’t save your choice. Please try again.');
    } finally {
      lock.current = false;
      if (alive.current) setSaving(false);
    }
  };
  return <div className="community-app cs-welcome"><main className="cs-welcome-screen" style={{'--cs-poster': `url(${mediaRoot}/wildlife-mosaic.jpg)`}}>
    <WildlifeMosaic active={active} />
    <div className="cs-welcome-shade" aria-hidden="true" />
    <section className="cs-welcome-content" aria-labelledby="cs-welcome-title">
      <h1 id="cs-welcome-title">Help Spot<br /><em>Real Animals!</em></h1>
      <p>Help needed: We have cameras all over the bush spotting animals.<br />From honey badgers to lions.<br />There are thousands of sightings, and we need your help finding them!</p>
      <div className="cs-welcome-actions">
        <button type="button" className="cs-welcome-tutorial" disabled={!active || saving} onClick={onTutorial}>Tutorial</button>
        <button type="button" className="cs-welcome-spotting" disabled={!active || saving} onClick={startSpotting}>{saving ? 'Starting…' : 'Start spotting'}</button>
      </div>
      {error && <p className="cs-welcome-error" role="alert">{error}</p>}
    </section>
  </main></div>;
}
